import { type FC, useCallback, useMemo } from "react"
import { Navigate, useParams } from "react-router"

import { parseNumber } from "@forento/core/utilities/number"

import EditStudentEmail, { type EmailSaveData } from "~/components/EditStudentEmail"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const EditProductAccessEmail: FC = () => {
	const emailId = parseNumber(useParams().emailId)

	const user = useUser().user!
	const platform = usePlatform()!

	const { data: layout, error: layoutError } = query.studentEmails.getLayout.useQuery()
	const { data: email, error: emailError, refetch } = query.studentEmails.getProductAccess.useQuery(emailId ?? -1)

	const placeholders = useMemo(
		() => [
			{ key: "firstName", value: user.firstName },
			{ key: "lastName", value: user.lastName },
			{ key: "productName", value: email?.product.name ?? "" },
			{ key: "productLink", value: `https://${platform.domain.platform}${email?.product.url ?? ""}` },
		],
		[email, platform.domain.platform, user],
	)

	const handleSave = useCallback(
		async (data: EmailSaveData) => {
			if (email == null || data === null) return
			await trpc.studentEmails.updateProductAccess.mutate({
				id: email.id,
				data: { title: data.title, content: data.content },
			})
			await refetch()
		},
		[email, refetch],
	)
	if (email === null) {
		return <Navigate to={routes.settings.studentEmail.productAccess()} replace />
	}

	return (
		<Layout>
			<PageBreadcrumb
				title={email?.product.name ?? "..."}
				path={[
					{ title: "Settings", link: routes.settings.index() },
					{ title: "Email Automation", link: routes.settings.studentEmail.index() },
					{ title: "Product Access", link: routes.settings.studentEmail.productAccess() },
				]}
			/>
			{layoutError || emailError ? (
				<p>Failed to load product access email.</p>
			) : !layout || !email ? (
				<PartialLoadingPage />
			) : (
				<EditStudentEmail
					email={email}
					placeholders={placeholders}
					layout={layout.content}
					aiTarget={{ type: "product-access", id: email.id }}
					onSave={handleSave}
				/>
			)}
		</Layout>
	)
}

export default EditProductAccessEmail
